import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  SectionHead,
  Card,
  GridGlow,
  TwoSidedCut,
  AgentReorder,
  buttonStyles,
  fadeUp,
  stagger,
  inView,
} from "@comai-portal/design-system";
import { PromptCard, PROMPT_A } from "./AskYourAgentPage";
import { CUSTOMER_PLATFORMS } from "../content/platforms";
import {
  HERO_EYEBROW,
  HERO_H1_LINE_1,
  HERO_H1_LINE_2_PRE,
  HERO_H1_HIGHLIGHT,
  HERO_H1_LINE_2_POST,
  HERO_LEAD,
  HERO_CTA_INSTALL,
  HERO_CTA_START,
  HERO_TRUST_LINE,
} from "../content/homepage-copy";

/**
 * HomePage — rebuilt from scratch on the shared ComOS design system. (CO 069)
 *
 * Leads with the entry-risk inversion: set up your whole business free; you
 * don't buy a Com until the work actually needs one. Two depths — accessible
 * lead, operator-respecting proof below. Reflects the mission pair: claims that
 * survive the closest read / infrastructure you can verify.
 *
 * Section order is the blueprint contract (CO 069, extended by CO 494 Phase 4):
 *  1 hero/thesis · 1b ask your agent · 2 what free means · 3 the two-sided cut ·
 *  4 free fleet + governance · 4b conversation is the console · 5 instant
 *  agent exposure · 5b what one network unlocks · 5c a federation of
 *  federations · 6 any platform · 7 verify · 8 close.
 *
 * 1b (AskYourAgent) surfaces the CO 257 one-sentence prompt (PROMPT_A) directly
 * under the hero: one fetch, no OAuth, seconds to run — the visitor's own agent
 * reports what ComOS is and what it costs (the trusted-messenger route). The
 * heavyweight build prompt (PROMPT_BUILD, CO 321) moved to /start and
 * /ask-your-agent §7, where intent is already established — the home page routes
 * curiosity, not commitment (Ron, 2026-08-03). Prompt text and the PromptCard
 * are contract-fixed and imported from AskYourAgentPage — never re-authored here.
 */
export function HomePage() {
  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      <Hero />
      <AskYourAgent />
      <WhatFreeMeans />
      <TheCut />
      <FleetAndGovernance />
      <ConversationConsole />
      <AgentExposure />
      <WhatItUnlocks />
      <FederationOfFederations />
      <AnyPlatform />
      <Verify />
      <Close />
    </div>
  );
}

/* ── 1. Hero — the thesis ─────────────────────────────────────────── */
function Hero() {
  return (
    <section className="relative overflow-hidden">
      {/* structural background — engineered dot-grid + ground glow */}
      <GridGlow />
      <Container className="relative pt-24 pb-20 md:pt-32 md:pb-28 text-center">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="mx-auto max-w-4xl"
        >
          <motion.p variants={fadeUp} className="ds-eyebrow mb-6">
            {HERO_EYEBROW}
          </motion.p>
          {/* Tall headline (scaleY 1.2), tight line-height, the two sentences as
              separate lines with a small gap between them. (CO 069; hero
              re-led with the install door per Ron 2026-09-06 — the portal's
              purpose is growing the network by node installs.) */}
          <motion.h1
            variants={fadeUp}
            className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5vw,3.75rem)] leading-[1.05] origin-top [transform:scaleY(1.2)]"
          >
            <span className="block">{HERO_H1_LINE_1}</span>
            <span className="block mt-[0.6em]">
              {HERO_H1_LINE_2_PRE}
              <span className="text-signal dark:text-signal-bright">
                {HERO_H1_HIGHLIGHT}
              </span>
              {HERO_H1_LINE_2_POST}
            </span>
          </motion.h1>
          <motion.p
            variants={fadeUp}
            className="text-lead text-mute dark:text-mute-soft mt-7 mx-auto max-w-2xl"
          >
            {HERO_LEAD}
          </motion.p>
          <motion.div
            variants={fadeUp}
            className="mt-10 flex flex-col sm:flex-row gap-3 justify-center"
          >
            <Link
              to="/install"
              className={buttonStyles({ variant: "primary", size: "lg" })}
            >
              {HERO_CTA_INSTALL}
            </Link>
            <Link
              to="/start"
              className={buttonStyles({ variant: "outline", size: "lg" })}
            >
              {HERO_CTA_START}
            </Link>
          </motion.div>
          <motion.p
            variants={fadeUp}
            className="mt-6 text-sm text-mute dark:text-mute-soft"
          >
            {HERO_TRUST_LINE}
          </motion.p>
        </motion.div>
      </Container>
    </section>
  );
}

/* ── 1b. Ask your agent — the trusted-messenger route ──────────────────
 * Surfaces the CO 257 one-sentence prompt (PROMPT_A) right under the hero:
 * one fetch, seconds to run, no OAuth — the agent reports back what ComOS is
 * and what it costs. The build prompt (PROMPT_BUILD) lives on /start and
 * /ask-your-agent §7. Prompt + PromptCard are contract-fixed and imported —
 * never re-authored. Deep link to /ask-your-agent for the full walkthrough. */
function AskYourAgent() {
  return (
    <Section tint="sunk">
      <Container width="prose">
        <SectionHead
          eyebrow="Or don't take our word for it"
          title="Point your own agent at ComOS. Let it tell you what we are."
          lead="Paste one sentence into the agent you already run — Claude, ChatGPT, OpenClaw, anything that can read a URL. It takes seconds, and the report is the pitch, in the one voice you already trust."
        />
        <motion.div {...inView} variants={fadeUp} className="mt-10">
          <PromptCard
            eyebrow="Paste this into your agent"
            prompt={PROMPT_A}
            after="No signup, no account, no MCP configuration — your agent reads our full machine-legible disclosure and tells you what ComOS is, exactly what it charges, and whether it's worth connecting to. When you're ready to build, the door prompt is on the Start page."
          />
        </motion.div>
        <motion.div
          {...inView}
          variants={fadeUp}
          className="mt-8 flex flex-wrap gap-3"
        >
          <Link
            to="/ask-your-agent"
            className={buttonStyles({ variant: "ghost" })}
          >
            See how it works, step by step →
          </Link>
          <Link to="/start" className={buttonStyles({ variant: "ghost" })}>
            Ready to build? The door prompt →
          </Link>
        </motion.div>
      </Container>
    </Section>
  );
}

/* ── 2. What "free" actually means ────────────────────────────────── */
function WhatFreeMeans() {
  const items = [
    {
      k: "Set up costs nothing",
      v: "Catalog, checkout, email, shipping, payments — wire up the entire operation. Setup isn't flow, so setup is free.",
    },
    {
      k: "Existing costs nothing",
      v: "No rent. No subscription. No seat fee. An idle account is a $0 account, this month and every month.",
    },
    {
      k: "The first Com is bought at the first real action",
      v: "When an agent sends the email, buys the label, makes the purchase — that's when money enters, and only then.",
    },
  ];
  return (
    <Section tint="sunk">
      <Container>
        <SectionHead
          eyebrow="What “free” actually means"
          title="Most platforms charge you before you earn. We charge only after value moves."
          lead="“Free to start” usually means a clock that runs out. This is different. You can set up your entire business and run it day to day without paying us anything — nothing to expire, and no bill until money actually moves."
        />
        <motion.div
          {...inView}
          variants={stagger}
          className="mt-12 grid gap-5 md:grid-cols-3"
        >
          {items.map((it) => (
            <motion.div key={it.k} variants={fadeUp}>
              <Card hover className="h-full">
                <h3 className="text-display-sm mb-2">{it.k}</h3>
                <p className="text-mute dark:text-mute-soft">{it.v}</p>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </Container>
    </Section>
  );
}

/* ── 3. The two-sided cut (the signature diagram) ─────────────────── */
function TheCut() {
  return (
    <Section id="cut">
      <Container>
        <SectionHead
          align="center"
          eyebrow="How we get paid"
          title="We make money only when you do."
          lead="No subscription, no monthly fee. We take a small percentage of the money that moves through your business — a little when you spend to run it, a little when a customer pays you. That's it. The rate is the same for every business, and it gets smaller as more businesses join."
        />
        <motion.div {...inView} variants={fadeUp} className="mt-14">
          <Card className="max-w-4xl mx-auto">
            <TwoSidedCut />
          </Card>
        </motion.div>
        <motion.div {...inView} variants={fadeUp} className="mt-8 text-center">
          <Link to="/pricing" className={buttonStyles({ variant: "ghost" })}>
            See exactly how it works →
          </Link>
        </motion.div>
      </Container>
    </Section>
  );
}

/* ── 4. The free fleet + governance ───────────────────────────────── */
function FleetAndGovernance() {
  return (
    <Section tint="sunk">
      <Container>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHead
              eyebrow="A fleet that runs your business"
              title="Dozens of agents, working for you — free."
              lead="Cart recovery, inventory, pricing, support. They watch, decide, and act before you've made a sale. Watching and deciding cost nothing; you pay only when one takes a real action — the same small cut as any expense."
            />
          </div>
          <motion.div {...inView} variants={stagger} className="grid gap-4">
            {[
              {
                k: "You set how far each one can act",
                v: "Off → Recommend → Confirm → Auto, per domain. Start recommend-only; promote to autonomous when you trust it.",
              },
              {
                k: "Every decision is logged",
                v: "Confidence score, the data behind it, the action taken — a full audit trail. Accountability, not a black box.",
              },
              {
                k: "Reversible by design",
                v: "Risk thresholds gate the big moves behind your approval; time-windowed rollback undoes an action after the fact.",
              },
              {
                k: "One ledger, both economies",
                v: "Agent spend and human sales settle on the same books. One tenant, one set of accounts — reconciled, not stitched together from separate tools.",
              },
            ].map((it) => (
              <motion.div key={it.k} variants={fadeUp}>
                <Card className="h-full">
                  <h3 className="text-display-sm mb-1.5">{it.k}</h3>
                  <p className="text-mute dark:text-mute-soft text-sm">
                    {it.v}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </Container>
    </Section>
  );
}

/* ── 4b. The conversation is the console (Ron 2026-09-06) ──────────────
 * No dashboards is literal: the human manager app was removed (CO 321);
 * the entire control plane is MCP tools, so the operator's agent renders
 * any report or action surface on demand. Series twin: fof-06. */
function ConversationConsole() {
  const chips = [
    "“What came in overnight?”",
    "“Reprice the slow movers.”",
    "“Chart this month against last.”",
    "“Chase the late shipment.”",
  ];
  return (
    <Section>
      <Container width="prose" className="text-center">
        <motion.div {...inView} variants={fadeUp}>
          <p className="ds-eyebrow mb-5">No dashboards</p>
          <h2 className="text-display-md text-balance">
            The conversation is the console.
          </h2>
          <p className="text-lead text-mute dark:text-mute-soft mt-6">
            You run your business by talking to it. There's no control panel to
            learn, because there isn't one — the whole OS is MCP tools your
            agent calls. Need a report, a chart, an approval screen? Your agent
            builds the interface on the fly and throws it away when you're
            done. The UI is generated when you need it, never installed.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            {chips.map((c) => (
              <span
                key={c}
                className="inline-flex items-center rounded-pill border border-mute-line dark:border-ink-line px-4 py-2 font-display text-sm text-mute dark:text-mute-soft"
              >
                {c}
              </span>
            ))}
          </div>
          <div className="mt-8">
            <Link
              to="/blog/the-relationship-is-a-prompt"
              className={buttonStyles({ variant: "ghost" })}
            >
              Why the prompt replaced the dashboard →
            </Link>
          </div>
        </motion.div>
      </Container>
    </Section>
  );
}

/* ── 5. Instant agent exposure ────────────────────────────────────── */
function AgentExposure() {
  return (
    <Section>
      <Container>
        <SectionHead
          align="center"
          eyebrow="Found by every AI that shops"
          title="The moment you exist, every agent can buy from you."
          lead="MCP, UCP, the whole agent-commerce surface — live and free from day one. ChatGPT, Claude, any agent shopping the network reaches you with no integration work. Most stores are invisible to the agents about to do the buying. You won't be."
        />
        <motion.div
          {...inView}
          variants={fadeUp}
          className="mt-12 max-w-3xl mx-auto"
        >
          <Card className="bg-ink text-paper dark:bg-black font-mono text-sm overflow-x-auto">
            <p className="text-mute-soft mb-3">
              # Any AI agent, talking to your store
            </p>
            <p>
              <span className="text-flow-bright">POST</span>{" "}
              <span className="text-paper/80">
                https://mcp.comos-federation.com/mcp
              </span>
            </p>
            <p className="mt-3">
              <span className="text-signal-bright">catalog_search</span>
              {"({ query: "}
              <span className="text-flow-bright">"wireless headphones"</span>
              {" })"}
            </p>
            <p>
              <span className="text-signal-bright">order_create</span>
              {"({ items })  "}
              <span className="text-mute-soft">
                # reserve → price → settle, one atomic call
              </span>
            </p>
          </Card>
          <p className="text-center text-mute dark:text-mute-soft mt-6">
            Agents are a commodity. The network they shop on isn't — and every
            business that joins makes it worth more to every agent, and every
            agent worth more to every business.
          </p>
        </motion.div>
      </Container>
    </Section>
  );
}

/* ── 5b. What one network unlocks — the flagship agentic-commerce story ─
 * Honest framing: this is what the architecture ENABLES (an agent CAN close
 * the loop, under your decision engine), not a one-click live feature — the
 * agent buy-cycle is built and governed but still being wired end-to-end
 * (CO 094). Plain-language outcome, the mechanics live in the diagram. */
function WhatItUnlocks() {
  return (
    <Section>
      <Container>
        <SectionHead
          align="center"
          eyebrow="What one network unlocks"
          title="When your suppliers are on the network too, the busywork runs itself."
          lead="Say you run a clothing line and buy fabric to make your products. If your fabric supplier is also a tenant, your low-inventory agent can reorder the moment stock runs low — checked against the limits you set, settled in an instant for almost nothing, and recorded end to end. Not a spreadsheet, not a phone call, not a three-day wire."
        />
        <motion.div {...inView} variants={fadeUp} className="mt-12">
          <Card className="max-w-4xl mx-auto">
            <AgentReorder />
          </Card>
        </motion.div>
        <motion.p
          {...inView}
          variants={fadeUp}
          className="mt-8 text-center text-mute dark:text-mute-soft max-w-2xl mx-auto"
        >
          This is what a network of businesses — not a pile of disconnected apps
          — makes possible. The more of your partners are on it, the more of
          your operation can simply take care of itself.
        </motion.p>
      </Container>
    </Section>
  );
}

/* ── 5c. A federation of federations — the shape of the network ────────
 * CO 494 Phase 4. Derived from the three canonical sentences in
 * FEDERATION-OF-FEDERATIONS-POSITIONING-SEED.md (comai-plan). Both-halves
 * rule: the sovereignty half never appears without the one-economy half in
 * the same passage. No customer-node count implied; no emergent-behavior
 * claims — mechanisms and design only. Links the published FoF series. */
function FederationOfFederations() {
  return (
    <Section tint="sunk">
      <Container width="prose" className="text-center">
        <motion.div {...inView} variants={fadeUp}>
          <p className="ds-eyebrow mb-5">How the network is built</p>
          <h2 className="text-display-md text-balance">
            A federation of federations.
          </h2>
          <p className="text-lead text-mute dark:text-mute-soft mt-6">
            Every business that joins runs a complete world of its own — your
            platforms, your agents, your rules, in our cloud or on hardware you
            own — admitted to one network. You govern your world. The network
            holds the one thing that must never fork: a single economy, one
            mint and one settlement rail, so your world can trade with every
            other.
          </p>
          <p className="text-mute dark:text-mute-soft mt-5">
            The hub anchors worlds rather than operating them, and the network
            grows by admitting new ones. The Federations of Federations series
            walks the whole architecture, in plain language.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/install"
              className={buttonStyles({ variant: "primary" })}
            >
              Put your world on your own machine
            </Link>
            <Link
              to="/blog/every-node-is-its-own-world"
              className={buttonStyles({ variant: "ghost" })}
            >
              Read the series →
            </Link>
          </div>
        </motion.div>
      </Container>
    </Section>
  );
}

/* ── 6. Your business, any platform ───────────────────────────────────
 * Two DIFFERENT ideas, kept separate on purpose:
 *  - VENDOR platforms = what your business IS / what you sell on (a kind of
 *    business: store, dentist, restaurant, auto shop). The dentist/food/auto
 *    visitor finds themselves here.
 *  - CUSTOMER platforms = TOOLS you use to run it (email, shipping, …) — NOT
 *    "what your business is."
 * Source of truth: content/platforms.ts, which mirrors the federation registry
 * (src/search/seed-data.ts SEED_PLATFORMS). RUN_TOOLS is derived from it, not
 * hand-copied, so the two can no longer drift. Reconciled 2026-09-06.
 */
// What kind of business you can BE (vendor platforms). example = a plain-English
// "this is you" so a non-retail visitor recognizes themselves.
const BUSINESS_KINDS = [
  {
    name: "Retail",
    example: "A store — products, catalog, checkout",
    live: true,
  },
  {
    name: "Bookings",
    example: "Appointments — a dentist, salon, or studio",
    live: true,
  },
  {
    name: "Services",
    example: "A job shop — auto repair, a contractor",
    live: true,
  },
  {
    name: "Rentals",
    example: "A hire business — rent assets by the day",
    live: true,
  },
  {
    name: "Digital",
    example: "Licenses and downloads — sell with no shipping",
    live: true,
  },
  // CO 455: Food and Events are PRESETS — recipes composing live platforms
  // (food = retail + bookings + shipping; events = bookings + retail). A
  // restaurant or a promoter can run on ComOS today; the badge says how.
  {
    name: "Food",
    example: "A kitchen — made-to-order, run the line",
    live: true,
    preset: true,
  },
  {
    name: "Events",
    example: "A promoter — sell dated, limited admissions",
    live: true,
    preset: true,
  },
];
// Tools you ADD to run it (customer platforms). Never "what you are."
// Derived from the shared platform data so this list tracks the registry.
const RUN_TOOLS = CUSTOMER_PLATFORMS.map((p) => p.name);

function AnyPlatform() {
  return (
    <Section tint="sunk">
      <Container>
        <SectionHead
          eyebrow="Your business, any platform"
          title="You don't have to be a store. You can be whatever you are."
          lead="ComOS isn't only for retail. A dentist, a restaurant, an auto shop, a rental outfit — each one picks the kind of business it is, then adds the tools to run it. One account, one set of Coms across all of it. Set it up free; pay only when money moves."
        />

        {/* What kind of business you can be — the vendor platforms */}
        <motion.p
          {...inView}
          variants={fadeUp}
          className="mt-12 mb-5 ds-eyebrow"
        >
          Pick what your business is
        </motion.p>
        <motion.div
          {...inView}
          variants={stagger}
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          {BUSINESS_KINDS.map((b) => (
            <motion.div key={b.name} variants={fadeUp}>
              <Card hover className="h-full">
                <div className="flex items-center gap-2 mb-1.5">
                  <h3 className="text-display-sm">{b.name}</h3>
                  {(!b.live || "preset" in b) && (
                    <span className="rounded-pill border border-mute-line dark:border-ink-line px-2 py-0.5 text-[11px] uppercase tracking-wide text-mute dark:text-mute-soft">
                      {"preset" in b ? "Preset" : "Coming soon"}
                    </span>
                  )}
                </div>
                <p className="text-mute dark:text-mute-soft text-sm">
                  {b.example}
                </p>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Tools to run it — the customer platforms */}
        <motion.p
          {...inView}
          variants={fadeUp}
          className="mt-12 mb-4 ds-eyebrow"
        >
          Then add the tools to run it
        </motion.p>
        <motion.div
          {...inView}
          variants={stagger}
          className="flex flex-wrap gap-3"
        >
          {RUN_TOOLS.map((p) => (
            <motion.div key={p} variants={fadeUp}>
              <span className="inline-flex items-center rounded-pill border px-5 py-2.5 font-display text-sm border-mute-line dark:border-ink-line text-ink dark:text-paper">
                {p}
              </span>
            </motion.div>
          ))}
        </motion.div>

        <motion.div {...inView} variants={fadeUp} className="mt-8">
          <Link
            to="/platforms"
            className={buttonStyles({ variant: "outline" })}
          >
            Explore the platforms
          </Link>
        </motion.div>
      </Container>
    </Section>
  );
}

/* ── 7. Infrastructure you can verify (the mission) ───────────────── */
function Verify() {
  return (
    <Section>
      <Container width="prose" className="text-center">
        <motion.div {...inView} variants={fadeUp}>
          <p className="ds-eyebrow mb-5">Infrastructure you can verify</p>
          <h2 className="text-display-md text-balance">
            We don't ask you to trust the CEO. We ask you to read the
            evidence.
          </h2>
          <p className="text-lead text-mute dark:text-mute-soft mt-6">
            Every change to the OS passes a QC gate before it ships. The honesty
            of the system is observable as a live rate — not a promise. Claims
            that survive the closest read; infrastructure you can verify.
          </p>
          <div className="mt-8">
            <Link
              to="/honesty"
              className={buttonStyles({ variant: "ghost" })}
            >
              See the live honesty ratio →
            </Link>
          </div>
        </motion.div>
      </Container>
    </Section>
  );
}

/* ── 8. Close ─────────────────────────────────────────────────────── */
function Close() {
  return (
    <Section tint="ink">
      <Container className="text-center">
        <motion.div {...inView} variants={fadeUp} className="max-w-2xl mx-auto">
          <h2 className="text-display-lg text-balance text-paper">
            Set it all up free. Pay only when money moves.
          </h2>
          <p className="text-lead text-paper/70 mt-6">
            A real tenant, a live MCP endpoint, a fleet of agents, and the whole
            agent network — on day one, for nothing. The first Com is bought
            when the first real action runs.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/start"
              className={buttonStyles({ variant: "primary", size: "lg" })}
            >
              Start building — free
            </Link>
            <Link
              to="/pricing"
              className={buttonStyles({
                variant: "outline",
                size: "lg",
                className:
                  "border-paper/25 text-paper hover:text-white hover:border-paper",
              })}
            >
              How we get paid
            </Link>
          </div>
        </motion.div>
      </Container>
    </Section>
  );
}
